<template>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form">
				<h2 class="item" v-if="isCreatingViewMode">
					Новая сервисная операция
				</h2>
				<h2 class="item" v-else>
					Карточка сервисной операции <span>{{ serviceOperation?.name }}</span>
				</h2>
			</div>
		</el-col>		
	</el-row>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form">
				<el-form class="base-form" :model="ruleForm" ref="jobForm" status-icon :rules="rules">
					<el-form-item label="Операция" label-position="top" prop="name">
						<el-input v-model="ruleForm.name" />
					</el-form-item>

					<el-form-item label="Описание" label-position="top" prop="description">
						<el-input v-model="ruleForm.description"
							maxlength="255"
							show-word-limit
							type="textarea" />
					</el-form-item>		

					<el-form-item>
						<el-row style="width: 100%;">
							<el-col :span="5">
								<el-button type="primary" :disabled="asyncOperationPending" @click="onFormSubmitClick">
									{{ isCreatingViewMode ? 'Создать' : 'Сохранить' }}
								</el-button>
							</el-col>
							<el-col :span="5">
								<el-button @click="onFormCancelClick">
									Отменить
								</el-button>
							</el-col>
							<el-col :span="14">
								<div v-if="!isCreatingViewMode" style="text-align: right;">
									<el-button type="danger" :disabled="!isDeletionAllowed || asyncOperationPending" @click="onFormDeleteClick">
										Удалить сервисную операцию
									</el-button>
								</div>
							</el-col>
						</el-row>

						<el-row style="width: 100%">
							<el-col :span="10">
								&nbsp;
							</el-col>
							<el-col :span="14">
								<div v-if="!isCreatingViewMode && !isDeletionAllowed" style="text-align: right; line-height: 1rem; margin-top: 1rem">
									<el-text type="info" size="small">
										<el-icon><Opportunity /></el-icon>
										Операцию нельзя удалить - на нее ссылаются сервисные работы (ссылок {{referencedJobsCount}})
									</el-text>
								</div>
							</el-col>
						</el-row>
					</el-form-item>
				</el-form>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import CatalogPickerComponent from '@/components/CatalogPickerComponent.vue';
import { ElButton, ElTable, ElLink, FormInstance, ElNotification, ElMessageBox } from 'element-plus';
import { Opportunity } from '@element-plus/icons-vue';
import { reactive } from 'vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import JobOperationsApiHelper from '@/helpers/api/JobOperationsApiHelper';

interface IServiceJobOperationFormFields {
	id: number;
	name: string | null;
	description: string | null;
}

@Options({
	components: {
		ElButton, ElTable, ElLink, CatalogPickerComponent, Opportunity
	},
	props: {
		params: Array
	}
})
export default class ServiceJobOperationView extends Vue {
	/** params */
	params!: Array<any>;
	isCreatingViewMode = false;

	/** constants */
	serviceOperation: ServiceJobOperationModel | null = null;
	
	/** view-models */
	isDeletionAllowed = false;
	referencedJobsCount = 0;
	asyncOperationPending = false;
	ruleForm = reactive<IServiceJobOperationFormFields>({
		id: 0,
		name: null,
		description: null
	});

	rules = reactive({
		name: [
			{ required: true, message: 'Название операции не может быть пустым'},
			{ min: 1, max: 255, message: 'Название операции не может быть короче 1 и длинее 255 символов'},			
		],
		description: [
			{ max: 255, message: 'Описание операции не может быть длинее 255 символов'},			
		]
	});
	
	mounted() {
		this.initForm();
	}

	/**
	 * init form with service-job-operation's data from incoming params
	 */
	async initForm() {
		if (!this.params?.length) {
			alert('Wrong view arguments')
			return;
		}

		if (this.params[1] === true) {
			this.isCreatingViewMode = true;
			return;
		}

		if (!!this.params[0]?.id &&
			!!this.params[0]?.name) {
				this.serviceOperation = new ServiceJobOperationModel(this.params[0]);
				this.ruleForm.id = this.serviceOperation.id;
				this.ruleForm.name = this.serviceOperation.name;
				this.ruleForm.description = this.serviceOperation.description;
				this.isCreatingViewMode = false;

				const jobGroupsApiHelper = new JobOperationsApiHelper(store.getters.jwt);
				const relatedJobIdsToGroup = await jobGroupsApiHelper.getJobOperationsAsync(this.serviceOperation.id);
				this.referencedJobsCount = relatedJobIdsToGroup?.length ?? 0;
				this.isDeletionAllowed = relatedJobIdsToGroup?.length === 0;
				return;
		}
	}

	async onFormSubmitClick() {
		const form = this.$refs.jobForm as FormInstance;
		form.validate(async (valid) => {
			if (valid) {
				try {
					this.asyncOperationPending = true;
					if (this.isCreatingViewMode) {
						await this.addOperation();
					} else {
						await this.updateOperation();
					}
					ElNotification({
						message: `Сервисная операция успешно ${this.isCreatingViewMode ? 'создана' : 'обновлена'}`,
						type: 'success',
					});
					EventHelper.dispatchEvent('jobOperationsUpdate');
					this.closeCurrentTab();
				} catch (e: any) {
					if (e.code == 'ERR_NETWORK') {
						ElNotification({
							message: `Ошибка соединения, не удалось ${this.isCreatingViewMode ? 'создать' : 'обновить'} сервисную операцию`,
							type: 'error',
						});
					} else {
						ElNotification({
							message: `Ошибка ${this.isCreatingViewMode ? 'создания новой' : 'обновления'} сервисной операции`,
							type: 'error',
						});
					}
					console.error('Error submitting service-job-operation', e);
				}
				finally {
					this.asyncOperationPending = false;
				}
			}			
		});
	}

	onFormCancelClick() {
		this.closeCurrentTab();
	}

	/** deletes an existing service-job */
	async onFormDeleteClick(e: Event) {
		e.stopPropagation();

		const id = this.serviceOperation!.id;
		const jwt = store.getters.jwt;
		const jobGroupsApiHelper = new JobOperationsApiHelper(jwt);
		const relatedJobIdsToOperation = await jobGroupsApiHelper.getJobOperationsAsync(id);

		if (relatedJobIdsToOperation === null) {
			ElNotification({
				message: 'Ошибка удаления операции сервисной работы',
				type: 'error',
			});
			return;
		}

		if (relatedJobIdsToOperation.length == 0) {
			ElMessageBox.confirm(
				'Сервисная операция будет удалена', {
				confirmButtonText: 'OK',
				cancelButtonText: 'Отмена',
				type: 'error',
			})
			.then(() => this.deleteOperationAsync(id));
		} else {
			ElMessageBox.alert(
				`Невозможно удалить операцию '${this.serviceOperation!.name}', на нее ссылаются сервисные работы (${relatedJobIdsToOperation.length})`,
				'Удаление'
			);
		}
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}

	async addOperation() {
		const request = {
			name: this.ruleForm.name,
			description: this.ruleForm.description
		};
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
		await a.post('/api/servicejoboperations', request);
	}

	async updateOperation() {
		const request = {
			id: this.ruleForm.id,					
			name: this.ruleForm.name,
			description: this.ruleForm.description
		};
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
		await a.put('/api/servicejoboperations', request);
	}

	async deleteOperationAsync(operationId: number) {
		try {
			this.asyncOperationPending = true;
			const jwt = store.getters.jwt;
			const a = axiosHelper.createInstance(jwt);
			await a.delete(`/api/servicejoboperations/${operationId}`);
			ElNotification({
				title: 'Сервисная операция удалена',
				type: 'success',
			});
			EventHelper.dispatchEvent('jobOperationsUpdate');
			this.closeCurrentTab();	
			this.asyncOperationPending = false;
		} catch (e) {
			ElNotification({
				title: 'Ошибка, сервисная операция не была удалена',
				type: 'error',
			});
			console.error(`Error deleting service-job-operation #${operationId}`, e)
		} finally {
			this.asyncOperationPending = false;
		}
	}
}
</script>


<style scoped>
h2.item span {
	color: var(--el-color-primary);
}
</style>